import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useTheme } from "@mui/material";
import { useI18N } from "../../context/I18NContext";
import { durationFormat, moneyFormat } from "../../helpers/general";
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
export const PaymentOptionProviderExternal = ({ provider }) => {
    const { t } = useI18N();
    const theme = useTheme();
    return (_jsxs(_Fragment, { children: [t(provider === null || provider === void 0 ? void 0 : provider.label), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [" ", t('Betaal je afspraak op een andere wijze', "payment"), " "] })] }));
};
export const PaymentOptionProviderAlternate = ({ provider }) => {
    const { t } = useI18N();
    const theme = useTheme();
    return (_jsxs(_Fragment, { children: [t(provider === null || provider === void 0 ? void 0 : provider.label), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [t('Schaf een abonnement of strippenkaart aan'), " "] })] }));
};
export const PaymentOptionProviderNone = ({ provider }) => {
    const { t } = useI18N();
    const theme = useTheme();
    return (_jsxs(_Fragment, { children: [t(provider === null || provider === void 0 ? void 0 : provider.label), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [t('Betaal bij je afspraak', "payment"), " "] })] }));
};
/**
 * The "<method>_productsOnly" row: a required product forces something to be paid online, but the
 * appointment itself is skipped (payment = 'optional') or was never payable online at all
 * (payment = 'none') - see CheckoutController::_getCheckout(). The `provider` passed in here is
 * the plain, unsuffixed entry (Payment.tsx renders this alongside that provider's own row, from
 * the same `payment.providers` item) - its icon is used as-is, but its label is a full sentence
 * ("Betaal met iDeal"), not a bare name, so it cannot be dropped straight into "Betaal de rest met
 * %provider%" without repeating "Betaal met". Stripped here rather than hardcoding a name per
 * provider, since every provider's label follows that same "Betaal met <naam>" shape.
 */
export const PaymentOptionProviderProductsOnly = ({ provider }) => {
    const { t, ts } = useI18N();
    const theme = useTheme();
    const providerLabel = t(provider === null || provider === void 0 ? void 0 : provider.label, "payment");
    const providerName = providerLabel.replace(/^Betaal met\s+/i, '');
    return (_jsxs(_Fragment, { children: [t('Afspraak niet online afrekenen', "payment"), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [ts('Betaal de rest met %provider%', { provider: providerName }, "payment"), " "] })] }));
};
export const PaymentOptionSubscriptionUse = ({ subscription, addPayment, addSubscription, invoiceTotal, addSubscriptions, addProductPayment, providerLabel }) => {
    const { t, ts } = useI18N();
    const theme = useTheme();
    let cost = parseFloat(subscription.balance).toFixed(0).toString();
    if (cost === "-1")
        cost = t('onbeperkt');
    return (_jsxs(_Fragment, { children: [_jsxs(_Fragment, { children: [t('Gebruik je strippenkaart', "payment"), " ", subscription.title] }), _jsxs("span", { style: {
                    display: 'block',
                }, children: [addPayment && invoiceTotal && (_jsx(_Fragment, { children: ts('Betaal het restant van %total% bij', { total: moneyFormat(invoiceTotal) }) })), addSubscription && invoiceTotal && addSubscriptions && (_jsx(_Fragment, { children: ts('Koop %amount% %amount=1?strippenkaart:strippenkaarten% aan voor %total% voor het restant van de benodigde strippen', { total: moneyFormat(invoiceTotal), amount: addSubscriptions }) })), addProductPayment && providerLabel && (_jsx(_Fragment, { children: ts('Het restant betaal je met %provider%', { provider: providerLabel }) }))] }), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [subscription.endDate ? `${t('Geldig tot')} ${dayjs(subscription.endDate).locale('nl').format('D MMMM YYYY')}` : t('Onbeperkt geldig'), _jsx("br", {}), cost, " ", t('strippen beschikbaar')] })] }));
};
// how a limit of a membership reads behind the number of appointments, by the period of the limit
const LIMIT_PERIODS = {
    day: 'per dag',
    week: 'per week',
    '2week': 'per 2 weken',
    '4week': 'per 4 weken',
    month: 'per maand',
    '3month': 'per 3 maanden',
    '6month': 'per 6 maanden',
    year: 'per jaar'
};
export const PaymentOptionMembershipUse = ({ subscription, addPayment, invoiceTotal, exceeded, addProductPayment, providerLabel }) => {
    var _a, _b;
    const { t, ts } = useI18N();
    const theme = useTheme();
    return (_jsxs(_Fragment, { children: [_jsxs(_Fragment, { children: [t('Gebruik je abonnement', "payment"), " ", subscription.title] }), _jsxs("span", { style: {
                    display: 'block',
                }, children: [addPayment && !!invoiceTotal && (_jsx(_Fragment, { children: ts('Betaal het restant van %total% bij', { total: moneyFormat(invoiceTotal) }) })), addProductPayment && providerLabel && (_jsx(_Fragment, { children: ts('Het restant betaal je met %provider%', { provider: providerLabel }) })), exceeded && (
                    // the option is grayed out, and this says why: it is not to be missed
                    _jsx("span", { style: { color: theme.palette.error.main, fontWeight: 700 }, children: t('max bereikt', "payment") }))] }), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [subscription.endDate ? `${t('Geldig tot')} ${dayjs(subscription.endDate).locale('nl').format('D MMMM YYYY')}` : t('Onbeperkt geldig'), !!subscription.limitBooking && !!LIMIT_PERIODS[(_a = subscription.limitPeriod) !== null && _a !== void 0 ? _a : ''] && (_jsxs(_Fragment, { children: [_jsx("br", {}), ts('%amount% %amount=1?afspraak:afspraken% %period%', { amount: subscription.limitBooking, period: t(LIMIT_PERIODS[(_b = subscription.limitPeriod) !== null && _b !== void 0 ? _b : '']) })] }))] })] }));
};
export const PaymentOptionSubscriptionBuy = ({ subscription, addSubscription, invoiceTotal, addSubscriptions }) => {
    var _a, _b;
    const { t, ts } = useI18N();
    const theme = useTheme();
    let cost = parseFloat(subscription.balance).toFixed(0).toString();
    if (cost === "-1")
        cost = t('onbeperkt');
    return (_jsxs(_Fragment, { children: [_jsx(_Fragment, { children: subscription.title }), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [t('Strippenkaart'), ", ", cost, " ", t('strippen'), _jsx("br", {}), ((_a = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _a !== void 0 ? _a : '') !== '' && (_jsxs(_Fragment, { children: [t('Geldig voor'), " ", durationFormat((_b = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _b !== void 0 ? _b : '', { t, ts })] }))] })] }));
};
export const PaymentOptionMembershipBuy = ({ subscription, title }) => {
    var _a, _b;
    const { t, ts } = useI18N();
    const theme = useTheme();
    let _title = title
        ? title
        : subscription.title;
    return (_jsxs(_Fragment, { children: [_jsx(_Fragment, { children: _title }), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [t('Abonnement'), _jsx("br", {}), ((_a = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _a !== void 0 ? _a : '') !== '' && (_jsxs(_Fragment, { children: [t('Geldig voor'), " ", durationFormat((_b = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _b !== void 0 ? _b : '', { t, ts })] }))] })] }));
};
export const PaymentOptionMembershipMandate = ({ subscription, title }) => {
    var _a, _b, _c, _d, _e;
    const { t, ts } = useI18N();
    const theme = useTheme();
    let _title = title ? title : subscription.title;
    let intervalStr = {
        '1': 'Maandelijkse incasso',
        '3': 'Incasso iedere 3 maanden',
        '6': 'Incasso iedere 6 maanden',
        '12': 'Jaarlijkse incasso'
    };
    return (_jsxs(_Fragment, { children: [_jsx(_Fragment, { children: _title }), _jsxs("span", { style: {
                    color: theme.palette.text.disabled,
                    display: 'block'
                }, children: [t('Abonnement'), _jsx("br", {}), (subscription === null || subscription === void 0 ? void 0 : subscription.initialFee) && (subscription === null || subscription === void 0 ? void 0 : subscription.initialFee) > 0 && (_jsxs(_Fragment, { children: [t('Eenmalig'), " ", moneyFormat((_a = subscription === null || subscription === void 0 ? void 0 : subscription.initialFee) !== null && _a !== void 0 ? _a : 0), _jsx("br", {})] })), intervalStr[(_c = (_b = subscription === null || subscription === void 0 ? void 0 : subscription.intervals) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '1'], _jsx("br", {}), ((_d = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _d !== void 0 ? _d : '') !== '' && (_jsxs(_Fragment, { children: [t('Geldig voor'), " ", durationFormat((_e = subscription === null || subscription === void 0 ? void 0 : subscription.validDuration) !== null && _e !== void 0 ? _e : '', { t, ts })] }))] })] }));
};
export const PaymentOptionProviderRemote = ({ provider, fees, useDeposit }) => {
    const { t } = useI18N();
    const theme = useTheme();
    let feeStr = '', fee = fees[provider === null || provider === void 0 ? void 0 : provider.key];
    if (useDeposit && (fee === null || fee === void 0 ? void 0 : fee.deposit) && (fee === null || fee === void 0 ? void 0 : fee.deposit) > 0) {
        feeStr = ` (+${moneyFormat(fee.deposit / 100)} )`;
    }
    else if ((fee === null || fee === void 0 ? void 0 : fee.full) && (fee === null || fee === void 0 ? void 0 : fee.full) > 0) {
        feeStr = ` (+${moneyFormat(fee.full / 100)} )`;
    }
    return (_jsxs(_Fragment, { children: [t(provider === null || provider === void 0 ? void 0 : provider.label, "payment"), fee && (_jsx(_Fragment, { children: feeStr }))] }));
};
export const PaymentOptionProviderAlternates = ({ provider, fees, useDeposit }) => {
    const { t } = useI18N();
    const theme = useTheme();
    let feeStr = '', fee = fees[provider === null || provider === void 0 ? void 0 : provider.key];
    if (useDeposit && (fee === null || fee === void 0 ? void 0 : fee.deposit) && (fee === null || fee === void 0 ? void 0 : fee.deposit) > 0) {
        feeStr = ` (+${moneyFormat(fee.deposit / 100)} )`;
    }
    else if ((fee === null || fee === void 0 ? void 0 : fee.full) && (fee === null || fee === void 0 ? void 0 : fee.full) > 0) {
        feeStr = ` (+${moneyFormat(fee.full / 100)} )`;
    }
    return (_jsxs(_Fragment, { children: [t(provider === null || provider === void 0 ? void 0 : provider.label, "payment"), fee && (_jsx(_Fragment, { children: feeStr }))] }));
};
